import React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { formatCurrency, formatDate, formatAccountDisplayName } from '../../utils/scanReportUtils';

/**
 * BalanceComparisonTable renders statement-level balance comparisons between
 * the deep scan and the summary scan for matched accounts.
 *
 * Accepts the `statement_comparison.by_account` array from the JSON.
 *
 * @param {Object} props
 * @param {Array} props.byAccount - Array of per-account comparison objects
 * @param {Array} [props.accounts] - Optional array of account objects for display names
 */
const BalanceComparisonTable = ({ byAccount, accounts = [] }) => {
  if (!byAccount || byAccount.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No statement comparisons available.
      </Typography>
    );
  }

  // Build a lookup for account display names by index
  const getAccountLabel = (accountIndex) => {
    const account = accounts[accountIndex];
    return account ? formatAccountDisplayName(account) : `Account #${accountIndex + 1}`;
  };

  // Flatten all matched statements across accounts for the table
  const rows = byAccount.flatMap((acct) =>
    (acct.comparison?.matched_statements || []).map((stmt) => ({
      accountLabel: getAccountLabel(acct.plan_scan_account_index),
      last4: acct.account_number_last4,
      endDate: stmt.end_date,
      beginBalanceScan: stmt.balance_comparison?.begin_balance_plan1,
      beginBalanceSummary: stmt.balance_comparison?.begin_balance_plan2,
      beginDiff: stmt.balance_comparison?.begin_balance_diff,
      endBalanceScan: stmt.balance_comparison?.end_balance_plan1,
      endBalanceSummary: stmt.balance_comparison?.end_balance_plan2,
      endDiff: stmt.balance_comparison?.end_balance_diff,
      match: stmt.balance_comparison?.balances_match,
    }))
  );

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No matched statements to compare.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell>Period End</TableCell>
            <TableCell align="right">Begin Balance</TableCell>
            <TableCell align="right">End Balance</TableCell>
            <TableCell align="center">Match</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow
              key={`${row.last4}-${row.endDate}-${idx}`}
              sx={{
                bgcolor: row.match === false ? 'error.50' : 'inherit',
                '&:last-child td': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  *{row.last4}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatDate(row.endDate)}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {formatCurrency(row.beginBalanceScan)}
                </Typography>
                {row.beginDiff !== 0 && row.beginDiff != null && (
                  <Typography variant="caption" color="error.main">
                    Δ {formatCurrency(row.beginDiff)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {formatCurrency(row.endBalanceScan)}
                </Typography>
                {row.endDiff !== 0 && row.endDiff != null && (
                  <Typography variant="caption" color="error.main">
                    Δ {formatCurrency(row.endDiff)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                {row.match ? (
                  <Chip
                    icon={<CheckCircleIcon fontSize="small" />}
                    label="Match"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<WarningIcon fontSize="small" />}
                    label="Mismatch"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

BalanceComparisonTable.propTypes = {
  byAccount: PropTypes.arrayOf(
    PropTypes.shape({
      plan_scan_account_index: PropTypes.number,
      account_number_last4: PropTypes.string,
      comparison: PropTypes.shape({
        matched_statements: PropTypes.array,
      }),
    })
  ),
  accounts: PropTypes.array,
};

export default BalanceComparisonTable;